import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  trackRegistrationStart,
  trackRegistrationError,
  trackSignupComplete,
  getAttributionParams,
  isInternalTestMode,
} from "../utils/analytics";
import {
  validateCompanyName,
  validateWorkEmail,
  normalizeRegistrationError,
} from "../utils/registrationValidation";
import neuBirdLogo from "../assets/NeuBird-Horizontal-Default.svg";
import laptopDemoImg from "../assets/laptop-demo.png";

const PASSWORD_RULES = [
  { key: "length", label: "At least 8 characters", test: (pw) => pw.length >= 8 },
  { key: "lower", label: "Lower case letters (a-z)", test: (pw) => /[a-z]/.test(pw) },
  { key: "upper", label: "Upper case letters (A-Z)", test: (pw) => /[A-Z]/.test(pw) },
  { key: "number", label: "Numbers (0-9)", test: (pw) => /[0-9]/.test(pw) },
  {
    key: "special",
    label: "Special characters (e.g. !@#$%^&*)",
    test: (pw) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pw),
  },
];

const BENEFITS = [
  {
    icon: "clock",
    title: "RCA in Minutes",
    body: "Evidence backed RCA across traces, logs, metric & config — automatically",
  },
  {
    icon: "filter",
    title: "Reduce Alert Noise",
    body: "Turn thousands of signals into 1–3 likely causes. No more chasing false positives at 3am.",
  },
  {
    icon: "headset",
    title: "Senior-level Insight",
    body: "Every on-call engineer gets the context and guidance they need. No escalation required.",
  },
  {
    icon: "shield",
    title: "Security-first Deploy",
    body: "SaaS or your own AWS VPC / Azure VNET. Read-only access. SOC 2 Type II. SSO/SAML.",
  },
];

const RegistrationForm = ({ marketplaceToken }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showPwInfo, setShowPwInfo] = useState(false);

  useEffect(() => {
    trackRegistrationStart();
  }, []);

  const API_URL = process.env.REACT_APP_API_URL || "";
  const DEV_MODE = process.env.REACT_APP_DEV_MODE === "true";

  const validatePassword = (pw) => {
    const lengthOk = PASSWORD_RULES[0].test(pw);
    const categoryPasses = PASSWORD_RULES.slice(1).filter((r) => r.test(pw)).length;
    return lengthOk && categoryPasses >= 3;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
    } else if (!isInternalTestMode()) {
      const companyResult = validateCompanyName(
        formData.company.trim(),
        formData.email.trim()
      );
      if (!companyResult.ok) {
        newErrors.company = companyResult.error;
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (formData.email.trim().length > 254) {
      newErrors.email = "Email must be 254 characters or less";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    } else if (!isInternalTestMode()) {
      const emailResult = validateWorkEmail(formData.email.trim());
      if (!emailResult.ok) {
        newErrors.email = emailResult.error;
      }
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!validatePassword(formData.password)) {
      newErrors.password = "Password does not meet the requirements";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setSubmitError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      trackRegistrationError("validation_failed");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const utmParams = getAttributionParams();
      const payload = {
        token: marketplaceToken || null,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        company: formData.company.trim(),
        email: formData.email.trim(),
        password: formData.password,
        ...(isInternalTestMode() && { internal: true }),
        ...(utmParams.utm_source && { utm_source: utmParams.utm_source }),
        ...(utmParams.utm_medium && { utm_medium: utmParams.utm_medium }),
        ...(utmParams.utm_campaign && { utm_campaign: utmParams.utm_campaign }),
        ...(utmParams.utm_content && { utm_content: utmParams.utm_content }),
      };

      const response = await axios.post(API_URL, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.success) {
        const signupMethod = marketplaceToken ? "aws_marketplace" : "direct_website";
        trackSignupComplete({ method: signupMethod });
        const deploymentStatus = {
          deploymentName: response.data.deployment?.deployment_name,
          runId: response.data.deployment?.runId,
          loginUrl: response.data.deployment?.login_url || "https://app.neubird.ai",
          startedAt: Date.now(),
        };
        sessionStorage.setItem(
          "nb_registration_deployment",
          JSON.stringify(deploymentStatus)
        );
        navigate("/registrations/complete", { state: deploymentStatus });
      } else {
        const errorMsg = normalizeRegistrationError(
          response.data.error || "Registration failed. Please try again."
        );
        trackRegistrationError("api_error");
        setSubmitError(errorMsg);
      }
    } catch (error) {
      console.error("Registration error:", error);
      let errorMsg = "Registration failed. Please try again.";
      let errorType = "network_error";

      if (error.response && error.response.data) {
        errorMsg = normalizeRegistrationError(
          error.response.data.error ||
            error.response.data.message ||
            "Registration failed. Please try again."
        );
        errorType = error.response.status === 409 ? "duplicate_company" : "api_error";
      }

      trackRegistrationError(errorType);
      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const pw = formData.password;
  const categoryPasses = PASSWORD_RULES.slice(1).filter((r) => r.test(pw)).length;

  return (
    <>
      <nav className="site-nav site-nav--logo-only">
        <a className="nav-logo" href="https://neubird.ai">
          <img src={neuBirdLogo} alt="NeuBird AI" />
        </a>
      </nav>

      <main className="page page-v2">
        <section className="form-column">
          <div className="form-column-inner">
            <header className="form-header">
              <h1>
                Stop Investigating.
                <br />
                Start <em>Resolving</em>.
              </h1>
              <div className="step-chevrons" aria-hidden="true">
                <div className="step-chevron">Connect</div>
                <div className="step-chevron">Replay</div>
                <div className="step-chevron">Get Full RCA</div>
              </div>
            </header>

            {DEV_MODE && (
              <div className="info-box dev-banner">
                <p>
                  <strong>Development Mode:</strong> AWS Marketplace validation is disabled for testing.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="registration-form">
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="firstName">
                    First Name<span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={errors.firstName ? "error" : ""}
                    placeholder="Sarah"
                    disabled={isSubmitting}
                    autoComplete="given-name"
                  />
                  <div className="error-message">{errors.firstName || ""}</div>
                </div>

                <div className="field">
                  <label htmlFor="lastName">
                    Last Name<span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={errors.lastName ? "error" : ""}
                    placeholder="Chen"
                    disabled={isSubmitting}
                    autoComplete="family-name"
                  />
                  <div className="error-message">{errors.lastName || ""}</div>
                </div>

                <div className="field full">
                  <label htmlFor="company">
                    Company Name<span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className={errors.company ? "error" : ""}
                    placeholder="Acme Corp"
                    disabled={isSubmitting}
                    autoComplete="organization"
                  />
                  <span className="field-hint">Used to create your secure workspace</span>
                  <div className="error-message">{errors.company || ""}</div>
                </div>

                <div className="field full">
                  <label htmlFor="email">
                    Work Email<span className="req">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? "error" : ""}
                    placeholder="sarah@acmecorp.com"
                    disabled={isSubmitting}
                    autoComplete="email"
                    maxLength={254}
                  />
                  <span className="field-hint">You will receive access and setup instructions here</span>
                  <div className="error-message">{errors.email || ""}</div>
                </div>

                <div className="field">
                  <label htmlFor="password" className="label-with-info">
                    Create Password<span className="req">*</span>
                    <button
                      type="button"
                      className="pw-info-btn"
                      aria-label="Password requirements"
                      onMouseEnter={() => setShowPwInfo(true)}
                      onMouseLeave={() => setShowPwInfo(false)}
                      onFocus={() => setShowPwInfo(true)}
                      onBlur={() => setShowPwInfo(false)}
                    >
                      i
                    </button>
                    {showPwInfo && (
                      <div className="pw-info-tooltip" role="tooltip">
                        <div className={`pw-rule ${PASSWORD_RULES[0].test(pw) ? "pass" : ""}`}>
                          {PASSWORD_RULES[0].label}
                        </div>
                        <div className={`pw-rule ${categoryPasses >= 3 ? "pass" : ""}`}>
                          At least 3 of: lowercase, uppercase, numbers, special characters
                        </div>
                      </div>
                    )}
                  </label>
                  <div className="password-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                      className={errors.password ? "error" : ""}
                      placeholder="••••••••••••••••••"
                      disabled={isSubmitting}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="pw-toggle pw-toggle-icon"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" stroke="currentColor" strokeWidth="2" />
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-8-10-8a18.45 18.45 0 0 1 5.06-5.94" stroke="currentColor" strokeWidth="2" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19" stroke="currentColor" strokeWidth="2" />
                          <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {(passwordFocused || pw.length > 0) && (
                    <div className="pw-requirements">
                      <div className={`pw-rule ${PASSWORD_RULES[0].test(pw) ? "pass" : ""}`}>
                        {PASSWORD_RULES[0].test(pw) ? "\u2713" : "\u2022"} {PASSWORD_RULES[0].label}
                      </div>
                      <div className={`pw-rule ${categoryPasses >= 3 ? "pass" : ""}`}>
                        {categoryPasses >= 3 ? "\u2713" : "\u2022"} At least 3 of the following:
                      </div>
                      {PASSWORD_RULES.slice(1).map((rule) => (
                        <div key={rule.key} className={`pw-rule pw-sub ${rule.test(pw) ? "pass" : ""}`}>
                          {rule.test(pw) ? "\u2713" : "\u2022"} {rule.label}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="error-message">{errors.password || ""}</div>
                </div>

                <div className="field">
                  <label htmlFor="confirmPassword">
                    Confirm Password
                  </label>
                  <div className="password-wrapper">
                    <input
                      type={showConfirm ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={errors.confirmPassword ? "error" : ""}
                      placeholder="••••••••••••••••••"
                      disabled={isSubmitting}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="pw-toggle pw-toggle-icon"
                      onClick={() => setShowConfirm(!showConfirm)}
                      tabIndex={-1}
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                    >
                      {showConfirm ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" stroke="currentColor" strokeWidth="2" />
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-8-10-8a18.45 18.45 0 0 1 5.06-5.94" stroke="currentColor" strokeWidth="2" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19" stroke="currentColor" strokeWidth="2" />
                          <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="error-message">{errors.confirmPassword || ""}</div>
                </div>
              </div>

              {submitError && (
                <div className="error-message submit-error">{submitError}</div>
              )}

              <button type="submit" className="cta-btn" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="loading">⏳</span>
                    Verifying company...
                  </>
                ) : (
                  "start free trial"
                )}
              </button>

              <p className="cta-meta">
                <strong>Instant Access</strong> &middot; No Credit Card &middot; 2-min setup
              </p>

              <div className="security-row security-row-v2">
                <div className="sec-badge-v2">Read-only access</div>
                <div className="sec-badge-v2">SOC 2 Type II</div>
                <div className="sec-badge-v2">SSO/SAML</div>
                <div className="sec-badge-v2">Data encrypted</div>
              </div>

              <p className="privacy-line">
                By starting a trial you agree to our{" "}
                <a href="https://neubird.ai/terms" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>{" "}
                and{" "}
                <a href="https://neubird.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </p>
            </form>

            <div className="testimonials-row">
              <div className="testimonial-card">
                <p className="testimonial-card-text">
                  &ldquo;Our team was able to get up and running with NeuBird rapidly. It is like having
                  an always-on SRE that delivers real-time incident diagnosis and actionable fixes
                  24/7—saving hours of troubleshooting and improving service quality.&rdquo;
                </p>
                <div className="testimonial-card-logo deephealth">deephealth</div>
              </div>
              <div className="testimonial-card">
                <p className="testimonial-card-text">
                  &ldquo;Our team was able to get up and running with NeuBird rapidly. It is like having
                  an always-on SRE that delivers real-time incident diagnosis and actionable fixes
                  24/7—saving hours of troubleshooting and improving service quality.&rdquo;
                </p>
                <div className="testimonial-card-logo snowflake">snowflake</div>
              </div>
            </div>
          </div>
        </section>

        <aside className="marketing-column">
          <div className="benefits-card">
            <h2 className="benefits-title">What customers measure after just one week</h2>
            <ul className="benefits-list">
              {BENEFITS.map((item) => (
                <li key={item.title} className="benefit-item">
                  <div className={`benefit-icon benefit-icon-${item.icon}`} aria-hidden="true" />
                  <div>
                    <div className="benefit-name">{item.title}</div>
                    <div className="benefit-desc">{item.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="laptop-visual">
            <img src={laptopDemoImg} alt="NeuBird dashboard on laptop" />
          </div>
        </aside>
      </main>
    </>
  );
};

export default RegistrationForm;
